import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Share2, Users, X } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function InviteModal({ open, onOpenChange }: InviteModalProps) {
  const { toast } = useToast();

  const { data: referralData } = useQuery<{ code: string }>({
    queryKey: ['/api/referrals/code'],
  });

  const referralCode = referralData?.code || '';

  const getReferralUrl = (code: string) => `${window.location.origin}/?ref=${code}`;
  const getShareMessage = (code: string) => `Discover great food on Eatzy Kudoz! Use my referral code: ${code} or check it out here: ${getReferralUrl(code)}`;

  const trackInvitation = async (method: string) => {
    try {
      await apiRequest('POST', '/api/referrals/invitations', { method });
    } catch (e) {}
  };

  const copyReferralLink = async () => {
    if (!referralCode) return;
    const url = getReferralUrl(referralCode);
    await navigator.clipboard.writeText(url);
    await trackInvitation('share_link');
    toast({ title: "Link Copied!", description: "Share this link with your friends" });
    onOpenChange(false);
  };

  const shareViaWhatsApp = async () => {
    if (!referralCode) return;
    const message = encodeURIComponent(getShareMessage(referralCode));
    await trackInvitation('whatsapp');
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareViaSMS = async () => {
    if (!referralCode) return;
    const message = encodeURIComponent(getShareMessage(referralCode));
    await trackInvitation('sms');
    window.open(`sms:?body=${message}`, '_blank');
  };

  const shareViaContacts = () => {
    toast({ title: "Coming Soon", description: "Contact sharing will be available soon" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Invite Friends
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Eatzy is better with friends! Share reviews, discover restaurants together, and earn Kudoz rewards.
        </p>
        <div className="space-y-2 mt-2">
          <button
            onClick={copyReferralLink}
            className="w-full flex items-center gap-3 p-3 rounded-md bg-card border hover-elevate active-elevate-2"
            data-testid="button-share-invite-link"
          >
            <div className="w-10 h-10 rounded-full bg-cyan-500/10 dark:bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Share2 className="w-5 h-5 text-cyan-500" />
            </div>
            <div className="flex-1 text-left">
              <span className="text-sm font-medium">Copy invite link</span>
              <p className="text-xs text-muted-foreground">Share your personal link</p>
            </div>
          </button>

          <button
            onClick={shareViaWhatsApp}
            className="w-full flex items-center gap-3 p-3 rounded-md bg-card border hover-elevate active-elevate-2"
            data-testid="button-invite-whatsapp"
          >
            <div className="w-10 h-10 rounded-full bg-green-500/10 dark:bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <FaWhatsapp className="w-5 h-5 text-green-500" />
            </div>
            <div className="flex-1 text-left">
              <span className="text-sm font-medium">WhatsApp</span>
              <p className="text-xs text-muted-foreground">Send via WhatsApp message</p>
            </div>
          </button>

          <button
            onClick={shareViaSMS}
            className="w-full flex items-center gap-3 p-3 rounded-md bg-card border hover-elevate active-elevate-2"
            data-testid="button-invite-text"
          >
            <div className="w-10 h-10 rounded-full bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex-1 text-left">
              <span className="text-sm font-medium">Text message</span>
              <p className="text-xs text-muted-foreground">Send via SMS</p>
            </div>
          </button>

          <button
            onClick={shareViaContacts}
            className="w-full flex items-center gap-3 p-3 rounded-md bg-card border hover-elevate active-elevate-2"
            data-testid="button-invite-contacts"
          >
            <div className="w-10 h-10 rounded-full bg-purple-500/10 dark:bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-purple-500" />
            </div>
            <div className="flex-1 text-left">
              <span className="text-sm font-medium">From contacts</span>
              <p className="text-xs text-muted-foreground">Find friends already on Eatzy</p>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
